import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { usePermission } from '@/lib/permissions/use-permission';
import { formatDate } from '@/lib/utils/date';
import { useApiKeyHistoricalSummary } from '../api';
import { SUMMARY_COLUMNS } from '../columns';
import type { ApiKeySummaryRow } from '../types';

export function ApiKeyHistoricalSummaryPage() {
  const userId = useAuthStore((s) => s.userId);
  const search = useApiKeyHistoricalSummary();
  const rows = search.data ?? [];

  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [username, setUsername] = useState<string>('');
  const [hasSearched, setHasSearched] = useState(false);

  const canViewHistory = usePermission('Manage Api Key.Api Historical Summary', BASE_PERMISSION.VIEW);

  if (!canViewHistory) return <Navigate to="/" replace />;

  const handleSearch = () => {
    if (startDate && endDate && startDate > endDate) {
      toast.error('Start date cannot be after end date.');
      return;
    }
    setHasSearched(true);
    search.mutate(
      {
        userId: Number(userId),
        startDate: startDate ? formatDate(startDate) : '',
        endDate: endDate ? formatDate(endDate) : '',
        username: username.trim() || undefined,
      },
      {
        onError: () => toast.error('Could not fetch historical summary.'),
      },
    );
  };

  return (
    <FillPage>
      <PageHeader
        title="History"
        description="Look up usage rollups for a date range and optional username."
      />

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>
            Leave dates empty to fetch all snapshots.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_1fr_auto] sm:items-end">
            <div className="space-y-1.5">
              <Label htmlFor="start-date">Start date</Label>
              <DatePicker
                value={startDate}
                onChange={setStartDate}
                placeholder="Pick a start date"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="end-date">End date</Label>
              <DatePicker
                value={endDate}
                onChange={setEndDate}
                placeholder="Pick an end date"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="username">Username (optional)</Label>
              <Input
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Filter by username"
              />
            </div>
            <Button onClick={handleSearch} disabled={search.isPending}>
              {search.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              Search
            </Button>
          </div>
        </CardContent>
      </Card>

      <DataTable<ApiKeySummaryRow, unknown>
        tableId="api-keys:historical:v1"
        columns={SUMMARY_COLUMNS}
        data={rows}
        isLoading={search.isPending}
        searchPlaceholder="Filter rows…"
        emptyTitle={hasSearched ? 'No rows match the filter' : 'Pick a range to begin'}
        emptyDescription={
          hasSearched
            ? 'Try widening the date range or removing the username filter.'
            : 'Choose start / end dates above and click Search.'
        }
        getRowId={(row) => String(row.id)}
        exportFilename="api-key-historical-summary.csv"
        pageSize={10}
      />
    </FillPage>
  );
}
